import { differenceInHours, differenceInMinutes, isBefore } from 'date-fns'
import { parseDate } from '@/dates'
import type {
	HostReviewContext,
	HostReviewFolder,
	HostReviewFolderKind,
	HostReviewThumbnails
} from '@/features/host-review/types'
import i18n from '@/translations/i18n'

/**
 * Time left in the review window, in hours while at least one is left and in
 * minutes below that. Floored, never below a minute, so the countdown stays
 * positive right up to expiry instead of flipping to "0".
 */
export const getReviewCountdown = (deadline: Date) => {
	const expiresAt = parseDate(deadline)
	const now = new Date()

	const hoursLeft = differenceInHours(expiresAt, now)
	if (hoursLeft >= 1) {
		return i18n.t('common:hours_short', { hours: hoursLeft })
	}

	const minutesLeft = differenceInMinutes(expiresAt, now)
	return i18n.t('common:minutes_short', {
		minutes: minutesLeft < 1 ? 1 : minutesLeft
	})
}

/** Whether the deadline has already passed, so the sweep is due to close it. */
export const isReviewOverdue = (deadline: Date) =>
	isBefore(parseDate(deadline), new Date())

export const findFolder = (
	folders: HostReviewFolder[],
	kind: HostReviewFolderKind
) => folders.find((folder) => folder.kind === kind)

/**
 * The largest thumbnail on offer, since size keys are backend-defined and not
 * guaranteed to include any particular one. Returns undefined for a photo with
 * no thumbnails at all rather than picking a broken src.
 */
export const getThumbnailUrl = (thumbnails: HostReviewThumbnails) => {
	const entries = Object.entries(thumbnails)
	if (entries.length === 0) {
		return undefined
	}

	const preferred = ['large', 'medium', 'original', 'small', 'thumb']
	for (const key of preferred) {
		if (thumbnails[key]) {
			return thumbnails[key]
		}
	}

	return entries[0][1]
}

/**
 * A review is finished from the operator's point of view once it has a closed
 * timestamp. Trusted over the photoset step, which still reads
 * `pending_host_review` on a full rejection.
 */
export const isReviewClosed = (review: HostReviewContext | null) =>
	Boolean(review?.closedAt)
